import { KalturaAssetReferenceType } from '@kaltura-ott/ott-client-types';
import {
  cloneObject,
  getAssetByIdOrExternalId,
  getMediaAsset,
  KuxLinearType,
  KuxProgramType,
  PREDEFINED_REFERENCES,
} from '@kaltura-ott/tvpil';
import { observable, computed } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container, singleton } from 'tsyringe';

import type { HeroRailStoreV2 } from '../';
import { storage } from '../../rhino/data/compat/storage';
import { onUpdateLastWatched } from '../../rhino/data/lastWatched';
import { fetchLastWatchedQuery } from '../../rhino/data/lastWatched/queries';
import { DmsStore } from '../dms';
import { addCurrentProgramToLinear, creatorEntityStore } from '../entities/entities.service';
import { Linear } from '../entities/Linear';
import type { LinearRepository } from '../linearRepository/linear-repository';

/**
 * Data type for storing information about the last watched content in persistent cache
 */
export type LastWatchedIdEntry = {
  linearId: number;
  subLinearExternalId?: string;
  subProgramExternalId?: string;
};

/**
 *
 */
type LastWatchedEntry = {
  linear: Linear;
  subLinearExternalId?: string;
  subProgramExternalId?: string;
};

/**
 * Manages last watched linear asset. Stores it in cache and restores item automatically
 * @category Store
 */
@singleton()
export class LastWatchedLinearStore {
  @observable.ref lastLinear?: Linear;
  private currentHeroRailReferenceId?: string;
  @observable.ref private lastWatchedLinearMap: Record<string, LastWatchedEntry> = {};
  // Link to the hero rail on the main page
  @observable.ref mainHeroRail?: HeroRailStoreV2;
  @observable isReadyInit = false;
  @observable isReadySetupDefaultChannels = false;

  /**
   * Set current hero rail pageReferenceId on rails and get in another places default hero pageReferenceId rail as default
   *
   * @param pageReferenceId
   */
  public setCurrentHeroRail(pageReferenceId?: string) {
    this.currentHeroRailReferenceId = pageReferenceId;
  }

  /**
   * Clear current hero rail when leave page
   */
  clearCurrentHeroRail() {
    this.currentHeroRailReferenceId = undefined;
  }

  /**
   * Get last Linear by hero rail pageReferenceId
   *
   * @param pageReferenceId
   */
  public getLastLinear(pageReferenceId: string): LastWatchedEntry | undefined {
    return computed(() => this.lastWatchedLinearMap[pageReferenceId]).get();
  }

  @computed
  public get isReady(): boolean {
    return this.isReadyInit && this.isReadySetupDefaultChannels;
  }

  /**
   * Defining default channels for no saved last watched or parse existed from local storage
   * @param heroStores subtype === UIDAPresentationTypeEnum.hero
   */
  async init() {
    try {
      const cacheData = await fetchLastWatchedQuery();

      this.lastWatchedLinearMap = { ...this.lastWatchedLinearMap, ...(await this.transformDataFromCache(cacheData)) };
    } catch (error) {
      console.warn(error);
    }
    this.isReadyInit = true;
  }

  public setMainHeroRail(mainHeroRail?: HeroRailStoreV2) {
    this.mainHeroRail = mainHeroRail;
  }

  public async setupDefaultChannels(heroStores: HeroRailStoreV2[]) {
    this.isReadySetupDefaultChannels = false;

    const cacheData = await fetchLastWatchedQuery();

    const needToLoad: Record<string, LastWatchedIdEntry> = {};
    heroStores.forEach((store) => {
      if (!cacheData[store.pageReferenceId] && store.defaultChannel) {
        needToLoad[store.pageReferenceId] = { linearId: store.defaultChannel };
      }
    });
    if (Object.keys(needToLoad).length) {
      this.lastWatchedLinearMap = { ...this.lastWatchedLinearMap, ...(await this.transformDataFromCache(needToLoad)) };
    }
    this.isReadySetupDefaultChannels = true;
  }

  private async getPermanentChannel(mosaicLinear: Linear) {
    let linear: Linear;
    const permanentProgramExternalId = mosaicLinear.currentProgram?.data.mosaicInfo?.sourceProgramExternalId;

    if (permanentProgramExternalId) {
      const permanentProgram = await lastValueFrom(
        getAssetByIdOrExternalId<KuxProgramType>({
          externalId: permanentProgramExternalId,
        }),
      );

      if (permanentProgram) {
        const kuxLinear = await lastValueFrom(
          getMediaAsset<KuxLinearType>(permanentProgram?.linearAssetId, KalturaAssetReferenceType.media),
        );

        if (kuxLinear) {
          const permanentLinear = creatorEntityStore(kuxLinear);
          linear = (await this.restoreAssets([permanentLinear]))?.[0];
        }
      }
    } else {
      console.log(
        // @ts-expect-error TODO Fix
        `Asset wasn't save to last watched - 'sourceProgramExternalId' wasn't defined for ${linear.currentProgram?.data.id}`,
      );
      return;
    }
    // @ts-expect-error TODO Fix
    return linear;
  }

  /**
   * save last watched liner on running playback from client
   * @param lastWatchedLinearId
   */
  async saveLastWatched({
    linear,
    subLinearExternalId,
    subProgramExternalId,
  }: {
    linear: Linear;
    subLinearExternalId?: string;
    subProgramExternalId?: string;
  }) {
    try {
      // @ts-expect-error TODO Fix
      if ((linear.isHiddenLinear || linear.isMosaicLinear) && !linear.currentProgram.isTypeVideo) {
        this.lastLinear = await this.getPermanentChannel(linear);
      } else {
        this.lastLinear = linear;
      }

      if (
        this.lastLinear &&
        this.currentHeroRailReferenceId &&
        this.currentHeroRailReferenceId !== PREDEFINED_REFERENCES.home
      ) {
        await this.saveLinear(
          {
            linear: this.lastLinear,
            subLinearExternalId,
            subProgramExternalId,
          },
          this.currentHeroRailReferenceId,
        );
      }

      //save for main hero
      if (this.lastLinear && !this.lastLinear.isAdult) {
        await this.saveLinear(
          {
            linear: this.lastLinear,
            subLinearExternalId,
            subProgramExternalId,
          },
          PREDEFINED_REFERENCES.home,
        );
      }
    } catch (error) {
      console.warn(error);
      throw new Error('no last viewed channel saved');
    }
  }

  /**
   * clear last watched data from client storage
   */
  async resetStore() {
    this.lastWatchedLinearMap = {};
    this.lastLinear = undefined;
    this.isReadyInit = false;
    this.isReadySetupDefaultChannels = false;

    await storage.removeLastWatched();
  }

  /**
   * get Linear by assetIDs
   */
  private async restoreAssets(assetsOrIds: number[] | Linear[]) {
    const linearRepository = container.resolve<LinearRepository>('linearRepository');

    const assets: Linear[] = assetsOrIds.map((assetOrId) =>
      cloneObject(assetOrId instanceof Linear ? assetOrId : linearRepository.getLinearById(assetOrId)!),
    );

    if (assets) {
      const dmsStore = container.resolve<DmsStore>('dmsStore');
      const uniqueAssets = assets.filter((value, index, array) => array.findIndex((v) => value.id === v.id) === index);
      await Promise.all([
        addCurrentProgramToLinear(assets, undefined, undefined, dmsStore.dms.mosaicInfo.isMosaicSupported),
        ...uniqueAssets.map((asset) => asset.setupRules()),
      ]);
    }
    return assets;
  }

  /**
   * save incoming linear in maps
   * @param linear
   * @param heroRailId
   */
  private async saveLinear(lastWatched: LastWatchedEntry, heroRailReferenceId: string) {
    const params = {
      referenceId: heroRailReferenceId,
      channelId: lastWatched.linear.id,
      subLinearExternalId: lastWatched.subLinearExternalId,
      subProgramExternalId: lastWatched.subProgramExternalId,
    };

    await onUpdateLastWatched(params);

    this.lastWatchedLinearMap = { ...this.lastWatchedLinearMap, [heroRailReferenceId]: lastWatched };
  }

  /**
   * Restoring assets by their ids.
   */
  private async transformDataFromCache(
    dataFromCache: Record<string, LastWatchedIdEntry>,
  ): Promise<Record<string, LastWatchedEntry>> {
    const linears = await this.restoreAssets(
      Object.values(dataFromCache).map((lastWatchedIdEntry) => lastWatchedIdEntry.linearId),
    );

    return Object.entries(dataFromCache).reduce(
      (acc: Record<string, LastWatchedEntry>, [pageReferenceId, linearIdEntry]) => {
        const linear = linears?.find((linear) => linear.id === linearIdEntry?.linearId);

        if (linear) {
          acc[pageReferenceId] = {
            linear,
            subLinearExternalId: linearIdEntry.subLinearExternalId,
            subProgramExternalId: linearIdEntry.subProgramExternalId,
          };
        }
        return acc;
      },
      {},
    );
  }

  /**
   * This transformation is used to prepare data for storage
   */
  private transformDataForSaving(dataForSaving: Record<string, LastWatchedEntry>): Record<string, LastWatchedIdEntry> {
    return Object.entries(dataForSaving).reduce((acc, [ref, { linear, subLinearExternalId, subProgramExternalId }]) => {
      return {
        ...acc,
        [ref]: {
          linearId: linear.id,
          subLinearExternalId,
          subProgramExternalId,
        },
      };
    }, {});
  }
}
